"use client";

import { makeObservable } from "@faire/web/common/makeObservable";
import { NumberParser } from "@faire/web/common/NumberParser";
import isInteger from "lodash/isInteger";
import { computed } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import styled, { css, keyframes } from "styled-components";

const NumberArray = Array.from(Array(10), (_, index: number) => 9 - index);

@observer
export class ScrollingNumber extends React.Component<{
  targetNumber: number | string;
  scrollDurationBasis: number;
  height: number;
  scrollUp: boolean;
  className?: string;
  omitSeparator?: boolean;
}> {
  private readonly numberParser = new NumberParser();

  static defaultProps = {
    scrollDurationBasis: 0.003,
    height: 24,
    scrollUp: false,
  };

  private renderNumeric = (currentDigit: string, index: number) => {
    const { height, scrollUp, omitSeparator } = this.props;
    const target = this.getTargetDigitPosition(Number(currentDigit));
    const isIndexZero = !index;
    const isDecimalPointVisited = this.targetNumberLengthBeforeDecimal < index;
    const shouldSeperate = !(
      (this.targetNumberLengthBeforeDecimal - index) %
      3
    );
    const shouldAddSeparator =
      !omitSeparator &&
      !isIndexZero &&
      !isDecimalPointVisited &&
      shouldSeperate &&
      this.isNumber;

    return (
      <>
        {shouldAddSeparator ? (
          <DigitContainer $height={height}>
            {this.numberParser.separator}
          </DigitContainer>
        ) : null}
        <DigitContainer $height={height}>
          <ScrollingContent
            $targetPosition={target}
            $scrollDurationBasis={this.props.scrollDurationBasis}
            $height={height}
            $scrollUp={scrollUp}
          >
            {NumberArray.map((value) => (
              <Digit $height={height} key={value}>
                {value}
              </Digit>
            ))}
          </ScrollingContent>
        </DigitContainer>
      </>
    );
  };

  private renderNonNumeric = (currentDigit: string) => {
    return (
      <DigitContainer $height={this.props.height}>
        {currentDigit === " " ? <>&nbsp;</> : currentDigit}
      </DigitContainer>
    );
  };

  constructor(props: {
    targetNumber: number | string;
    scrollDurationBasis: number;
    height: number;
    scrollUp: boolean;
    className?: string;
    omitSeparator?: boolean;
  }) {
    super(props);
    makeObservable(this);
  }

  render() {
    return (
      <ScrollingNumberWrapper className={this.props.className}>
        {new Array(this.targetNumberLength).fill("").map((_value, index) => {
          const currentDigit = this.targetNumber[index] ?? "";
          return (
            <React.Fragment key={index}>
              {isInteger(parseInt(currentDigit, 10))
                ? this.renderNumeric(currentDigit, index)
                : this.renderNonNumeric(currentDigit)}
            </React.Fragment>
          );
        })}
      </ScrollingNumberWrapper>
    );
  }

  @computed
  private get targetNumber() {
    return `${this.props.targetNumber ?? 0}`;
  }

  @computed
  private get targetNumberLengthBeforeDecimal() {
    const separatedTargetNumberString = this.targetNumber.split(".");
    return separatedTargetNumberString[0]?.length ?? 0;
  }

  @computed
  private get targetNumberLength() {
    return this.targetNumber.length;
  }

  @computed
  private get isNumber() {
    return typeof this.props.targetNumber === "number";
  }

  private getTargetDigitPosition = (currentDigit: number) => {
    return this.props.height * (9 - currentDigit);
  };
}

const DigitContainer = styled.div<{ $height: number }>`
  display: inline-block;
  height: ${(props) => props.$height}px;
  overflow: hidden;
  text-align: center;
`;

const scroll = (
  targetPosition: number,
  height: number,
  scrollUp: boolean
) => keyframes`
  from {
    transform: translateY(${scrollUp ? css`-${height * 9}px` : "0"});
  }
  to {
    transform: translateY(-${targetPosition}px);
  }
`;

const scrollAnimation = ({
  $targetPosition,
  $scrollDurationBasis,
  $height,
  $scrollUp,
}: {
  $targetPosition: number;
  $scrollDurationBasis: number;
  $height: number;
  $scrollUp: boolean;
}) => css`
  ${scroll($targetPosition, $height, $scrollUp)} ${($scrollUp
    ? $height * 9 - $targetPosition
    : $targetPosition) * $scrollDurationBasis}s ease-out;
`;

const ScrollingContent = styled.div<{
  $targetPosition: number;
  $scrollDurationBasis: number;
  $height: number;
  $scrollUp: boolean;
}>`
  transform: translateY(-${({ $targetPosition }) => $targetPosition}px);

  animation: ${scrollAnimation};
`;

export const Digit = styled.p<{
  $height: number;
}>`
  height: ${(props) => props.$height}px;
`;

const ScrollingNumberWrapper = styled.div`
  display: inline-flex;
`;
